import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  MenuBook as MenuBookIcon,
  Forum as ForumIcon,
  Work as WorkIcon,
  Groups as GroupsIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import UIBox from 'components/ui/UIBox';
import UIText from 'components/ui/UIText';

/**
 * Two kinds of rail items:
 *  - `group` — a labelled cluster with N inner ids (one item per row in the
 *    catalog). Renders the cluster's `label` as a sub-header and the
 *    children as a flat list of links.
 *  - `solo` — a standalone, prominent item with its own icon + accent. Used
 *    for the 4 "Окремі набори" coming-soon categories.
 *
 * Active highlighting follows the leaf id (group child or solo) currently in
 * the viewport.
 */

export type RailGroupChild = {
  id: string;
  title: string;
  /** Accent hex — controls active dot + label colour. */
  accent: string;
};

export type RailGroup = {
  kind: 'group';
  /** Cluster label, e.g. 'Слова', 'Дієслова', 'Вирази'. */
  label: string;
  children: RailGroupChild[];
};

export type RailSolo = {
  kind: 'solo';
  id: string;
  title: string;
  /** Material Symbols-style icon — drawn in an accent-tinted square. */
  Icon: typeof MenuBookIcon;
  accent: string;
  soon?: boolean;
};

export type RailItem = RailGroup | RailSolo;

interface CategoryRailProps {
  items: RailItem[];
  /**
   * Externally-driven preview highlight: the id of a category whose
   * catalog row the pointer is currently over. Lets the rail mirror
   * that hover so the two halves of /wordset feel like one surface.
   * When `null`, only the rail's own active/hover styles apply.
   */
  hoveredId?: string | null;
  /**
   * Called when the pointer enters/leaves a rail item. Lets the parent
   * synchronise the catalog rows back the other way (catalog row
   * darkens when its rail item is hovered).
   */
  onHover?: (id: string | null) => void;
  /**
   * Item text alignment within the rail / mobile chip strip. Default
   * 'left'. When 'right', item labels right-align and the active-state
   * side bar swaps to the right edge so it stays adjacent to the label.
   * The "Категорії" header always right-aligns regardless — only the
   * items below toggle.
   */
  align?: 'left' | 'right';
  /**
   * When true, inactive item labels render in muted secondary text and
   * only pick up their accent colour when the row is active (scroll-spy)
   * or under hover. Useful for single-accent rails (e.g. the families
   * letter rail) where every item shares one colour and the dim baseline
   * keeps the active row visually loud. Default false.
   */
  dimInactive?: boolean;
}

const hexToRgba = (hex: string, a: number) => {
  const h = hex.replace('#', '');
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(
    h.slice(2, 4),
    16,
  )}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
};

const flattenLeafIds = (items: RailItem[]): string[] =>
  items.flatMap((it) =>
    it.kind === 'group' ? it.children.map((c) => c.id) : [it.id],
  );

const CategoryRail: React.FC<CategoryRailProps> = ({
  items,
  hoveredId,
  onHover,
  align = 'left',
  dimInactive = false,
}) => {
  const alignRight = align === 'right';
  const leafIds = useMemo(() => flattenLeafIds(items), [items]);
  const [activeId, setActiveId] = useState<string>(leafIds[0] ?? '');
  const mobileStripRef = useRef<HTMLDivElement | null>(null);
  const desktopRailRef = useRef<HTMLDivElement | null>(null);

  // Keep the active chip centred in the horizontal strip when the user
  // scrolls the page. scrollIntoView would also disturb the parent's
  // vertical scroll on some browsers — `scrollTo` on the strip alone
  // avoids that and stays within the chip rail.
  useEffect(() => {
    const strip = mobileStripRef.current;
    if (!strip) return;
    const chip = strip.querySelector<HTMLElement>(
      `[data-chip-id="${activeId}"]`,
    );
    if (!chip) return;
    const target =
      chip.offsetLeft - strip.clientWidth / 2 + chip.clientWidth / 2;
    strip.scrollTo({ left: target, behavior: 'smooth' });
  }, [activeId]);

  // Mirror the same "keep the active item in view" rule for the desktop
  // rail. The rail itself is sticky with a capped max-height — if the
  // catalog is taller than the viewport, the rail content overflows into
  // an inner scroll. Sliding to the active item keeps the highlight
  // visible without forcing the user to manually scroll the rail.
  useEffect(() => {
    const rail = desktopRailRef.current;
    if (!rail) return;
    if (rail.scrollHeight <= rail.clientHeight) return; // fits — no need
    const item = rail.querySelector<HTMLElement>(
      `[data-leaf-id="${activeId}"]`,
    );
    if (!item) return;
    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    const viewTop = rail.scrollTop;
    const viewBottom = viewTop + rail.clientHeight;
    // Only scroll if the active item drifted out of the visible portion.
    // Centring it would feel jumpy on every scroll-spy tick; nudging
    // just enough to bring it back into view matches the natural read.
    if (itemTop < viewTop || itemBottom > viewBottom) {
      const target = itemTop - rail.clientHeight / 2 + item.offsetHeight / 2;
      rail.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
    }
  }, [activeId]);

  useEffect(() => {
    if (leafIds.length === 0) return;
    const lastId = leafIds[leafIds.length - 1];

    // Manual scroll-spy. Rule: active = section occupying the most
    // visible pixels in the area below the sticky header. Symmetric in
    // both scroll directions and naturally matches "the section the
    // user is reading" instead of brittle top-edge thresholds.
    //
    // Older edge-based rules misfired when scrolling up: a section's
    // top would re-enter the viewport (1 px past the header line) and
    // immediately steal the highlight, even though only a sliver was
    // visible. Comparing visible AREA fixes both directions at once.

    const computeActive = () => {
      // Top inset varies by breakpoint: desktop only hides behind the
      // 64 px AppHeader (+16 buffer), mobile also has the sticky chip
      // strip (~52 px) below the header. Computed inside so window
      // resizes naturally re-trigger the right offset.
      const isMobile = window.innerWidth < 900;
      const headerOffset = isMobile ? 120 : 80;
      // Bottom-of-page fallback — when the page is fully scrolled past
      // its content the last section may share viewport area equally
      // with what's above it; force the highlight to the last item so
      // the user always sees where they are in the rail.
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 4) {
        setActiveId(lastId);
        return;
      }

      const viewportH = window.innerHeight;
      let bestId = leafIds[0];
      let bestVisible = -1;
      for (const id of leafIds) {
        const el = document.getElementById(`ws-${id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        // Visible portion of the section inside the area below the
        // sticky header. Negative slice → 0.
        const visible = Math.max(
          0,
          Math.min(rect.bottom, viewportH) -
            Math.max(rect.top, headerOffset),
        );
        if (visible > bestVisible) {
          bestVisible = visible;
          bestId = id;
        }
      }
      setActiveId(bestId);
    };

    computeActive();
    window.addEventListener('scroll', computeActive, { passive: true });
    window.addEventListener('resize', computeActive);
    return () => {
      window.removeEventListener('scroll', computeActive);
      window.removeEventListener('resize', computeActive);
    };
  }, [leafIds]);

  const onSelect = (id: string) => (event: React.MouseEvent) => {
    const target = document.getElementById(`ws-${id}`);
    if (target) {
      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
    }
  };

  // Index of the first solo item — used to insert the "Окремі набори"
  // section divider before it.
  const firstSoloIdx = items.findIndex((it) => it.kind === 'solo');

  // ── Desktop rail ────────────────────────────────────────────────────────
  const renderDesktopGroup = (group: RailGroup) => (
    <UIBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {group.label ? (
        <UIText
          sx={{
            color: 'primary.subtitle',
            fontSize: 12,
            fontWeight: 700,
            pb: 0.5,
            px: 1.5,
          }}
        >
          {group.label}
        </UIText>
      ) : null}
      {group.children.map((child) => {
        const on = child.id === activeId;
        const peeked = !on && child.id === hoveredId;
        return (
          <Box
            component={'a'}
            data-leaf-id={child.id}
            key={child.id}
            href={`#ws-${child.id}`}
            onClick={onSelect(child.id)}
            onMouseEnter={() => onHover?.(child.id)}
            onMouseLeave={() => onHover?.(null)}
            sx={{
              alignItems: 'center',
              // Three visual layers: ACTIVE wins (0.10 tint + side bar),
              // PEEKED (peer hover from catalog row) and own hover share
              // a 0.06 tint that hints "you're pointing at this", and
              // INACTIVE stays transparent.
              bgcolor: on
                ? hexToRgba(child.accent, 0.1)
                : peeked
                ? hexToRgba(child.accent, 0.06)
                : 'transparent',
              borderRadius: 1.5,
              cursor: 'pointer',
              display: 'flex',
              fontSize: 14.5,
              fontWeight: 700,
              gap: 1.25,
              // Right-aligned rails push the label to the trailing edge
              // and mirror the active-state side bar so it stays next to
              // the label rather than floating off on the empty side.
              justifyContent: alignRight ? 'flex-end' : 'flex-start',
              position: 'relative',
              px: 1.5,
              py: 1,
              textDecoration: 'none',
              transition: 'background-color .14s ease',
              '&:hover': {
                bgcolor: on ? undefined : hexToRgba(child.accent, 0.06),
              },
            }}
          >
            {on && (
              <Box
                sx={{
                  bgcolor: child.accent,
                  borderRadius: 3,
                  bottom: 7,
                  ...(alignRight ? { right: 0 } : { left: 0 }),
                  position: 'absolute',
                  top: 7,
                  width: 3,
                }}
              />
            )}
            <Box
              component={'span'}
              className={'ws-rail-child-label'}
              sx={{
                color:
                  dimInactive && !on && !peeked
                    ? 'text.secondary'
                    : child.accent,
                transition: 'color .14s ease',
              }}
            >
              {child.title}
            </Box>
          </Box>
        );
      })}
    </UIBox>
  );

  const renderDesktopSolo = (solo: RailSolo) => {
    const on = solo.id === activeId;
    const peeked = !on && solo.id === hoveredId;
    return (
      <Box
        component={'a'}
        data-leaf-id={solo.id}
        key={solo.id}
        href={`#ws-${solo.id}`}
        onClick={onSelect(solo.id)}
        onMouseEnter={() => onHover?.(solo.id)}
        onMouseLeave={() => onHover?.(null)}
        sx={{
          alignItems: 'center',
          // Mirror the group-child rule so solo and group rows respond
          // identically to peer hover from the catalog side.
          bgcolor: on
            ? hexToRgba(solo.accent, 0.1)
            : peeked
            ? hexToRgba(solo.accent, 0.06)
            : 'transparent',
          borderRadius: 1.5,
          cursor: 'pointer',
          display: 'flex',
          gap: 1.25,
          position: 'relative',
          px: 1.5,
          py: 1.125,
          textDecoration: 'none',
          transition: 'background-color .14s ease',
          '&:hover': {
            bgcolor: on ? undefined : hexToRgba(solo.accent, 0.06),
            '& .ws-rail-solo-label': {
              color: solo.accent,
            },
          },
        }}
      >
        {on && (
          <Box
            sx={{
              bgcolor: solo.accent,
              borderRadius: 3,
              bottom: 7,
              left: 0,
              position: 'absolute',
              top: 7,
              width: 3,
            }}
          />
        )}
        <Box
          className={'ws-rail-solo-label'}
          component={'span'}
          sx={{
            color: solo.accent,
            flex: 1,
            fontSize: 14.5,
            fontWeight: 700,
            lineHeight: 1.2,
            minWidth: 0,
            transition: 'color .14s ease',
          }}
        >
          {solo.title}
        </Box>
        {solo.soon && (
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: hexToRgba(solo.accent, 0.12),
              borderRadius: 999,
              color: solo.accent,
              display: 'inline-flex',
              flex: 'none',
              fontSize: 10.5,
              fontWeight: 800,
              gap: 0.375,
              px: 0.875,
              py: 0.25,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 12 }} />
            {'Скоро'}
          </Box>
        )}
      </Box>
    );
  };

  // ── Mobile chips ────────────────────────────────────────────────────────
  // Flat horizontal strip — one chip per leaf id, accent-coloured when
  // active, dashed border when the leaf is coming-soon. Matches the
  // prototype's MobileNav.
  const renderMobileChip = (
    id: string,
    accent: string,
    title: string,
    soon: boolean,
  ) => {
    const on = id === activeId;
    return (
      <Box
        component={'a'}
        data-chip-id={id}
        key={id}
        href={`#ws-${id}`}
        onClick={onSelect(id)}
        sx={{
          alignItems: 'center',
          bgcolor: on ? accent : 'background.paper',
          border: '1px solid',
          borderColor: on ? accent : 'divider',
          borderRadius: 999,
          borderStyle: soon ? 'dashed' : 'solid',
          color: on ? '#fff' : 'text.secondary',
          cursor: 'pointer',
          display: 'inline-flex',
          flex: 'none',
          fontSize: 13.5,
          fontWeight: 700,
          gap: 0.875,
          px: 1.625,
          py: 1,
          textDecoration: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <Box
          sx={{
            // The chip's accent dot mirrors the desktop rail rule:
            // dim it to a neutral grey when the parent rail asks for
            // muted inactive items, so the chip reads as cold until
            // it's the section the user is currently in.
            bgcolor: on
              ? '#fff'
              : dimInactive
              ? 'text.secondary'
              : accent,
            borderRadius: '50%',
            height: 8,
            width: 8,
          }}
        />
        {title}
        {soon && (
          <ScheduleIcon
            sx={{
              color: on ? '#fff' : accent,
              fontSize: 14,
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      {/* Desktop: sticky rail capped at viewport height. The 80px top
          clears the sticky AppHeader (~64px) plus 16px breathing. If the
          catalog has more categories than fit in `100vh - 96px`, the
          rail content overflows into an inner scroll instead of being
          hidden below the viewport (which is what fully-rigid sticky
          used to do). A `useEffect` further down keeps the active item
          centred in this inner scroll as the page scrolls. */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <UIBox
          ref={desktopRailRef}
          sx={{
            alignSelf: 'start',
            maxHeight: 'calc(100vh - 96px)',
            overflowX: 'hidden',
            overflowY: 'auto',
            position: 'sticky',
            // Hide the scrollbar but keep the area scrollable — content
            // is still reachable via mouse-wheel / trackpad / keyboard
            // and the auto-scroll effect below brings the active item
            // into view as the page scrolls.
            scrollbarWidth: 'none',
            top: 80,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <UIText
            sx={{
              color: 'primary.subtitle',
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: '0.1em',
              mb: 1.5,
              pl: 0,
              pr: 1.5,
              textAlign: 'right',
              textTransform: 'uppercase',
            }}
          >
            {'Категорії'}
          </UIText>
          <UIBox
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}
          >
            {items.map((it, idx) => (
              <React.Fragment key={it.kind === 'group' ? it.label : it.id}>
                {/* Section divider before the first solo item */}
                {idx === firstSoloIdx && firstSoloIdx > 0 && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 1,
                      mt: 0.5,
                      pl: 1.5,
                      pr: 0.5,
                    }}
                  >
                    <UIText
                      sx={{
                        color: 'primary.subtitle',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {'Окремі набори'}
                    </UIText>
                    <Box
                      sx={{
                        bgcolor: 'divider',
                        flex: 1,
                        height: '1px',
                      }}
                    />
                  </Box>
                )}
                {it.kind === 'group'
                  ? renderDesktopGroup(it)
                  : renderDesktopSolo(it)}
              </React.Fragment>
            ))}
          </UIBox>
        </UIBox>
      </Box>

      {/* Mobile: sticky horizontal chip strip — flat list of every leaf
          id. Pinned under the AppHeader (56px at xs, 60px at sm) so the
          user can jump between categories while reading. The strip
          stays inside the UIContainer's content area (no edge-to-edge
          negative margins) — pulling it out leaked horizontal scroll
          to the whole page when the chip row was wider than the
          viewport. */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          display: { xs: 'block', md: 'none' },
          mb: 2,
          // Make sure the wrapper never grows wider than its parent —
          // belts-and-braces guard against a flex-overflow bubble.
          maxWidth: '100%',
          overflow: 'hidden',
          position: 'sticky',
          top: { xs: 56, sm: 60 },
          zIndex: (theme) => theme.zIndex.appBar - 1,
        }}
      >
        <UIBox
          ref={mobileStripRef}
          sx={{
            display: 'flex',
            gap: 1,
            // Mirror the desktop rail's alignment: when callers ask for a
            // right-aligned rail, the mobile chips push to the trailing
            // edge too (no effect when the chips overflow and the strip
            // scrolls — the user still drags freely either way).
            justifyContent: alignRight ? 'flex-end' : 'flex-start',
            // The inner scroller is the ONLY element allowed to scroll
            // horizontally on the page — everything else must constrain.
            overflowX: 'auto',
            overflowY: 'hidden',
            px: 1.25,
            py: 1.25,
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {items.map((it) => {
            if (it.kind === 'solo') {
              return renderMobileChip(it.id, it.accent, it.title, !!it.soon);
            }
            return it.children.map((c) =>
              renderMobileChip(c.id, c.accent, c.title, false),
            );
          })}
        </UIBox>
      </Box>
    </>
  );
};

export default CategoryRail;
// Re-export icon shortcuts so callers can stay focused on data
export {
  MenuBookIcon as RailMenuBookIcon,
  ForumIcon as RailForumIcon,
  WorkIcon as RailWorkIcon,
  GroupsIcon as RailGroupsIcon,
};
